import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { RefContextProvider, useRefContext } from "../../../context/RefContext";
import { useState, useCallback, useEffect, useRef } from "react";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";

export const RightSidebar: React.FC<{
	title: string;
	children: React.ReactNode;
	onClose: () => void;
	onExpand?: () => void;
	tools?: React.ReactNode;
	maximizeWidth?: boolean;
	isNewStyle?: boolean;
}> = ({
	title,
	children,
	onClose,
	onExpand,
	tools,
	maximizeWidth,
	isNewStyle,
}) => {
	const [width, setWidth] = useState(() =>
		getLocalStorage(
			`${title.split(" ").join("-")}-right-sidebar-width`,
			350
		)
	);
	const [isResizing, setIsResizing] = useState(false);
	const sidebarRef = useRef<HTMLDivElement>(null);

	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		setIsResizing(true);
		e.preventDefault();
	}, []);

	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (!isResizing || !sidebarRef.current) return;

			const rect = sidebarRef.current.getBoundingClientRect();
			const newWidth = rect.right - e.clientX;

			// Set min and max width constraints
			const minWidth = 300;
			const maxWidth = Math.min(800, window.innerWidth * 0.8);
			const clampedWidth = Math.max(
				minWidth,
				Math.min(maxWidth, newWidth)
			);

			setWidth(clampedWidth);
			setLocalStorage(
				`${title.split(" ").join("-")}-right-sidebar-width`,
				clampedWidth
			);
		},
		[isResizing]
	);

	const handleMouseUp = useCallback(() => {
		setIsResizing(false);
	}, []);

	useEffect(() => {
		if (isResizing) {
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);

			return () => {
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
			};
		}
	}, [isResizing, handleMouseMove, handleMouseUp]);

	return (
		<RefContextProvider>
			<div
				ref={sidebarRef}
				className={`h-full flex flex-col overflow-auto z-50 relative ${
					isNewStyle
						? "shadow-md rounded-lg border border-gray-200"
						: "border-l border-gray-200"
				}`}
				style={{
					width: maximizeWidth ? "100%" : `${width}px`,
					minWidth: "300px",
				}}
				data-testid={`right-sidebar-${title.split(" ").join("-")}`}
			>
				{/* Resize Handle */}
				{!maximizeWidth && (
					<div
						className="absolute top-0 left-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-500 hover:bg-opacity-50 z-10 transition-colors"
						onMouseDown={handleMouseDown}
					>
						<div className="w-full h-full flex items-center justify-center">
							<div className="w-0.5 h-8 bg-gray-300 opacity-0 hover:opacity-100 transition-opacity" />
						</div>
					</div>
				)}

				<div className="flex justify-between items-center sticky top-0 z-10 bg-white border-b border-gray-200 h-12 px-3 text-sm font-medium">
					<span className="text-gray-800 h-10 flex items-center">
						{title}
					</span>
					<div className="flex items-center">
						{tools}
						{onExpand && (
							<IconButton
								size="small"
								onClick={onExpand}
								data-testid="right-sidebar-open-in-full-button"
							>
								<OpenInFullIcon fontSize="small" />
							</IconButton>
						)}
						<IconButton
							size="small"
							data-testid="close-right-sidebar"
							onClick={onClose}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</div>
				</div>
				<SideBarChildren>{children}</SideBarChildren>
			</div>
		</RefContextProvider>
	);
};

const SideBarChildren: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const { container } = useRefContext();
	return (
		<div className="h-full flex flex-col" ref={container}>
			{children}
		</div>
	);
};
